import { useRef } from 'react';
import { ArtColoursProvider, lineOf, useArtColours, type ArtColourProps } from '../motion/art-colours';
import { posed, type HandRig, type Pose } from './high-five-poses';
import { Animate, AnimateTransform, SmilTimeline, usePlayOnceInView, type Keys } from '../motion/smil';

/** Seconds: the palms meet here, and the sparks fly from it. */
const CLAP = 0.8;
/** One-shot: it settles on the static pose before this. */
const DURATION = 1.3;
const SAND = '#FFF7ED';

/**
 * Hold apart, wind up, swing in and press past the resting pose into each other, as palms do in a real
 * high-five, then ease back apart onto it. `side` mirrors the right hand.
 */
function swing(side: 1 | -1, pivot: string): Keys {
	return [
		[0.3, `${-12 * side} ${pivot}`],
		[0.55, `${-16 * side} ${pivot}`, 'out'],
		[CLAP, `${4 * side} ${pivot}`, 'in'],
		[CLAP + 0.1, `${3 * side} ${pivot}`],
		[CLAP + 0.45, `0 ${pivot}`],
	];
}

/** The forearms travel too, or the hands only pivot on their cuffs like a hinge. */
function reach(side: 1 | -1): Keys {
	return [
		[0.3, `${-10 * side} 6`],
		[0.55, `${-14 * side} 9`, 'out'],
		[CLAP, `${9 * side} -2`, 'in'],
		[CLAP + 0.1, `${8 * side} -2`],
		[CLAP + 0.45, '0 0'],
	];
}

const LEFT_RIG: HandRig = {
	side: 1,
	knuckles: 150,
	axis: [150, 250],
	thumb: { base: [203, 207], box: { left: 197, right: 234, top: 162, bottom: 209 } },
};
const RIGHT_RIG: HandRig = { side: -1, knuckles: 160, axis: [262, 250] };
const APART: Pose = { curl: 4, turn: 14, thumb: -8 };
const WOUND_UP: Pose = { curl: 6, turn: 20, thumb: -12 };
/** The palms flatten against each other and the fingers flick back. */
const IMPACT: Pose = { curl: -4, turn: -5, thumb: 4 };

/** Every outline redrawn through the clap: relaxed apart, wound up, square on, flattened, square on. */
function PosedPath(props: { d: string; rig: HandRig; hand: boolean; fill?: string; stroke?: 'none' }) {
	const line = lineOf(useArtColours().ink, 1.5);
	const draw = (pose: Pose) => posed(props.d, props.rig, pose, props.hand);
	return (
		<path d={props.d} {...line} fill={props.fill} stroke={props.stroke ?? line.stroke}>
			<Animate
				attr="d"
				keys={[
					[0.3, draw(APART)],
					[0.55, draw(WOUND_UP), 'out'],
					[CLAP, props.d, 'in'],
					[CLAP + 0.07, draw(IMPACT), 'out'],
					[CLAP + 0.3, props.d],
				]}
			/>
		</path>
	);
}

/** Filled with the paper (the slide's colour), so it hides the other hand's thumb once the palms meet. */
const RIGHT_PALM =
	'M325.697 278.892C298.55 283.093 220.284 263.008 213.638 243.186C195.687 189.657 194.321 144.193 199.309 110.629C199.309 110.629 200.085 103.79 205.765 103.133C208.366 102.833 210.475 104.813 211.994 107.052C213.749 109.639 214.743 112.656 215.05 115.76C216.1 126.384 219.973 161.359 226.257 169.095';

/** Unstroked, inside the right palm's outline: it hides the left thumb while the palms press together. */
const RIGHT_PALM_INSIDE =
	'M213 243L205 190L203 150L205 110L226 169L244 168L264 164L293 186L304 205L290 207L325 279C298 283 240 268 213 243Z';

/** The outlines before these indices are the hand; the rest are the cuff, which has no fingers to move. */
const RIGHT_FINGERS = 4;
const LEFT_FINGERS = 5;

const RIGHT_HAND = [
	'M209.672 104.436C209.672 104.436 212.533 88.996 222.872 91.4621C225.459 92.0775 227.344 92.989 228.728 94.0764C232.89 97.3337 232.551 102.164 233.048 105.389C233.712 109.688 237.177 145.616 244.552 168.163',
	'M228.729 94.072C228.729 94.072 234.813 82.5602 245.593 89.3849C245.593 89.3849 250.618 92.3045 251.779 105.653C252.945 119.002 264.566 164.323 264.566 164.323',
	'M251.784 105.656C251.784 105.656 253.192 91.7526 263.075 93.3998C272.959 95.0423 273.623 103.815 274.087 110.047C274.552 116.28 279.8 168.337 293.571 186.026',
	'M286.074 171.305C286.074 171.305 292.823 150.553 308.577 149.933C313.175 149.753 316.942 153.672 316.556 158.239C316.264 161.718 313.486 166.715 310.235 173.85C301.039 194.032 304.044 204.878 304.044 204.878',
	'M290.166 207.45L348.074 202.3',
	'M324.433 283.198L350.034 195.583C350.498 193.996 351.924 192.881 353.578 192.802L394.849 189.66C397.478 189.54 399.461 192.002 398.779 194.528L374.836 282.902C374.381 284.591 372.843 285.762 371.088 285.762L328.158 288.145C325.571 288.145 323.709 285.674 324.433 283.198Z',
	'M330.944 260.928L380.966 258.934',
	'M342.737 221.311L392.253 218.317',
];

const LEFT_HAND = [
	'M118.447 103.343C118.447 103.343 121.164 90.6701 133.101 92.7799C145.037 94.8898 144.369 141.848 153.411 168.194',
	'M136.24 94.6081C136.24 94.6081 146.978 81.9534 155.199 98.4531C163.42 114.948 166.095 156.151 168.083 163.702',
	'M157.262 101.977C157.262 101.977 166.662 95.74 171.465 106.026C176.263 116.316 185.659 189.38 194.005 192.804L199.23 182.999C199.23 183 209.45 168.77 221.65 172.13C225.22 173.11 227.14 177.03 225.73 180.43C224.66 183.02 221.32 186.17 217.09 190.83C205.14 204.03 204.799 213.063 204.799 213.063',
	'M107.589 284.79C132.363 281.149 170.792 273.648 182.427 257.029C199.686 232.367 204.799 213.063 204.799 213.063',
	'M133.338 170.799C133.338 170.799 129.724 144.12 128.786 134.468C127.848 124.821 125.92 104.444 118.452 103.347C106.032 101.52 106.567 130.373 106.107 136.49C105.145 149.242 104.755 188.645 102.883 209.248C102.196 216.823 96.5622 223.023 89.0705 224.462L78.6807 226.461',
	'M23.6004 224.663L65.759 305.676C66.5207 307.142 68.137 307.966 69.7766 307.721L110.881 302.835C113.482 302.446 114.954 299.647 113.788 297.301L73.1067 215.224C72.3311 213.655 70.594 212.804 68.8755 213.142L26.2943 219.092C23.7583 219.592 22.4114 222.377 23.6004 224.663Z',
	'M44.8682 216.49L89.6788 305.35',
	'M38.4766 252.728L89.6781 248.925',
	'M53.5762 282.405L104.276 278.602',
	'M27.2979 230.26L78.4994 226.457',
];

/** Drawn from the clap outwards, so `pathLength` grows each one out of the contact point. */
const SPARKS = [
	{ d: 'M163.903 69.0181L155.204 56.8955', away: '-4.7 -6.5' },
	{ d: 'M187.247 61.177V39.2593', away: '0 -8' },
	{ d: 'M207.893 74.1072L223.424 56.6914', away: '5.3 -6' },
];

/** Drawn out of the contact point, then they spring back to where the static drawing has them. */
function Spark(props: { d: string; away: string }) {
	const accent = useArtColours().accent;
	return (
		<path d={props.d} pathLength={1} strokeDasharray="1 1" stroke={accent} strokeWidth="1.5" strokeLinecap="round">
			<Animate
				attr="stroke-dashoffset"
				keys={[
					[CLAP, 1],
					[CLAP + 0.15, 0, 'out'],
				]}
			/>
			<Animate
				attr="opacity"
				keys={[
					[CLAP - 0.01, 0],
					[CLAP, 1],
				]}
			/>
			<AnimateTransform
				type="translate"
				keys={[
					[CLAP, '0 0'],
					[CLAP + 0.14, props.away, 'out'],
					[CLAP + 0.4, '0 0'],
				]}
			/>
		</path>
	);
}

/** Plays once as the slide comes into view and ends on the pose of `AllianceIllustration` (`../static/alliance.tsx`). */
export function HighFive(props: ArtColourProps & { active: boolean; className?: string }) {
	return (
		<ArtColoursProvider colours={props} paper={SAND}>
			<HighFiveSvg active={props.active} className={props.className} />
		</ArtColoursProvider>
	);
}

function HighFiveSvg(props: { active: boolean; className?: string }) {
	const ref = useRef<SVGSVGElement>(null);
	const paper = useArtColours().paper;
	usePlayOnceInView(ref, props.active);
	return (
		<SmilTimeline seconds={DURATION} once>
			<svg ref={ref} viewBox="0 0 343 347" fill="none" aria-hidden className={props.className}>
				<g>
					<AnimateTransform type="rotate" keys={swing(1, '68 260')} />
					<AnimateTransform type="translate" additive keys={reach(1)} />
					{LEFT_HAND.map((d, i) => (
						<PosedPath key={d} d={d} rig={LEFT_RIG} hand={i < LEFT_FINGERS} />
					))}
				</g>
				<g>
					<AnimateTransform type="rotate" keys={swing(-1, '360 240')} />
					<AnimateTransform type="translate" additive keys={reach(-1)} />
					<PosedPath d={RIGHT_PALM_INSIDE} rig={RIGHT_RIG} hand fill={paper} stroke="none" />
					<PosedPath d={RIGHT_PALM} rig={RIGHT_RIG} hand fill={paper} />
					{RIGHT_HAND.map((d, i) => (
						<PosedPath key={d} d={d} rig={RIGHT_RIG} hand={i < RIGHT_FINGERS} />
					))}
				</g>
				{SPARKS.map((spark) => (
					<Spark key={spark.d} {...spark} />
				))}
			</svg>
		</SmilTimeline>
	);
}
